import React, { useContext } from "react"
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap"

import LlmInstrContext from "./LlmInstrContext"

/**
 * The only modal on the page: a confirm, not a step. It never navigates away from the editor,
 * and cancelling leaves the validation results on screen.
 *
 * Deliberately thin. The impact of the change is already on screen in the results grid
 * immediately behind this dialog, and restating it here only asked the reader to compare two
 * renderings of the same thing. What is left is the one fact the grid does not carry --
 * whether this save creates an override or edits the value in place -- plus the exact text
 * being written.
 */
function SaveDialog({ isOpen, onClose }) {
  const { resolved, draft, save, saving } = useContext(LlmInstrContext)

  const location = resolved?.response_context
  const instruction = resolved?.instruction
  if (!location) return null

  const confirm = async () => {
    const result = await save()
    if (result) onClose()
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {`Save instruction for ${location.tenant_shortname || location.tenant_id} · ${location.m_loc_id}?`}
      </ModalHeader>
      <ModalBody>
        {instruction?.save_creates_override && (
          <Alert color="info" className="small">
            {`The current value is ${instruction.source === "NONE" ? "unset" : "inherited"}. `}
            Saving creates a new tenant-level override; the inherited value is left untouched.
          </Alert>
        )}

        <div className="small text-muted mb-1">This text will be saved:</div>
        <pre className="border rounded p-2 small mb-0"
             style={{ whiteSpace: "pre-wrap", maxHeight: 220, overflow: "auto" }}>
          {draft}
        </pre>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="success" onClick={confirm} disabled={saving}>
          {saving ? <Spinner size="sm" /> : "Save"}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default SaveDialog
